/**
 * Renderer Section Registry (TSP-5 / TSP-6) — single source of truth
 * for the section ids the SiteRagReportView renderer emits AND the
 * subset that Template Studio's SectionsTab exposes for control
 * (visibility / drag-reorder / per-section page / heading override /
 * column override).
 *
 * Why this exists
 * ---------------
 * Before TSP-5 the Studio carried a hardcoded `NON_STUDIO_SECTION_IDS`
 * Set inside `SectionsTab.tsx` and the renderer carried hardcoded JSX
 * blocks with `data-section="<id>"` anchors — two independent lists
 * that drifted whenever a new section was added. The TSP audit found
 * 8 sections (verdict, keyFindings, btmOverview, appendix, conclusion,
 * radarChart, lcoeChart, extendedData) the renderer emits but the
 * Studio never controlled.
 *
 * This registry collapses the two lists into one. The Studio reads
 * `RENDERER_SECTIONS` filtered to `studioControllable: true`; the
 * `NON_STUDIO_SECTIONS` Set is derived from the same registry so
 * adding a new renderer-only section in one place automatically
 * flows to both consumers (and the registry-parity test fails CI if
 * a contributor forgets the `studioControllable` flag).
 *
 * Renderer-only entries
 * ---------------------
 * The 7 ids flagged `studioControllable: false` (cover, dataSources,
 * legalDisclaimer, closing, end, footer, recommendations) are emitted by
 * the renderer at fixed positions — drag-reorder / visibility toggles
 * are not meaningful for these:
 *   - `cover`        — always page 1; the cover layout variant is
 *                       controlled via the separate `coverLayout`
 *                       field on the template.
 *   - `dataSources`  — bound to the appendix block in
 *                       ReferencesSection; has no narrative.
 *   - `legalDisclaimer` — always-on limitation-of-liability notice.
 *   - `closing` — legacy end-of-report alias (renderer-only).
 *   - `footer`       — theme-driven, always at the bottom.
 *   - `recommendations` — legacy block kept for back-compat in the
 *                       page-3 risk grid; superseded by `actionPlan`.
 *
 * Renderer-only appendix tail (not in TOC / not in Studio):
 *   - `blindSpots`   — RV6 self-honesty block; always after appendix
 *   - `dataQualityNotes` — cross-validation flags under appendix
 *   - `scoreProvenance` — score lineage panel (sub-block after radar)
 *
 * Theme footer + legal disclaimer follow in ReportClosingTail (fixed tail).
 *
 * Categories
 * ----------
 * Used by the Studio's future grouping affordance (Header / Body /
 * Appendix / Footer). Categories are decorative for now — they don't
 * affect render order. The renderer still uses
 * `template.sectionOrder` / `sectionConfig` ordering.
 */

import { getSectionsForType } from './reportSections';
import type { ReportTypeId } from './reportTypes';

export type RendererSectionCategory = 'header' | 'body' | 'appendix' | 'footer' | 'sub';

export interface RendererSectionEntry {
  /** Stable section id — matches the `data-section="<id>"` attribute
   *  the renderer stamps on the section wrapper, the `sectionId` key
   *  used in `template.sections[]`, and the registry id used in
   *  `REPORT_SECTIONS`. */
  id: string;
  /** Human-readable label. Mirrors `defaultLabel` from
   *  `reportSections.ts` for studio-controllable ids. Renderer-only
   *  ids carry their own label for the registry's parity tests and
   *  documentation surfaces. */
  label: string;
  /** True when the Studio's SectionsTab lists this id for user
   *  control (drag, visibility, page, columns, heading override).
   *  False for system-fixed renderer-only ids. */
  studioControllable: boolean;
  /** True when the renderer ALWAYS emits this section regardless of
   *  `template.sections[].visible` (e.g. cover, footer, blindSpots).
   *  Studio-controllable + alwaysVisible: visible toggle is honoured
   *  but the section can never disappear from the export pipeline. */
  alwaysVisible: boolean;
  /** Display grouping for future SectionsTab affordances. */
  category: RendererSectionCategory;
  /** When set, this id is a nested visual/table block under a parent section. */
  parentSectionId?: string;
}

function categoryForSectionId(id: string): RendererSectionCategory {
  if (id === 'appendix' || id === 'blindSpots') return 'appendix';
  if (id === 'end') return 'footer';
  return 'body';
}

/** Renderer-only ids — not in the feasibility section registry. */
const RENDERER_ONLY_COVER: RendererSectionEntry = {
  id: 'cover',
  label: 'Cover',
  studioControllable: false,
  alwaysVisible: true,
  category: 'header',
};

const RENDERER_ONLY_TAIL: RendererSectionEntry[] = [
  { id: 'dataSources',     label: 'Data Sources',             studioControllable: false, alwaysVisible: true,  category: 'appendix' },
  { id: 'legalDisclaimer', label: 'Legal Disclaimer',         studioControllable: false, alwaysVisible: true,  category: 'footer' },
  { id: 'recommendations', label: 'Recommendations (legacy)', studioControllable: false, alwaysVisible: false, category: 'appendix' },
  { id: 'closing',         label: 'Closing',                  studioControllable: false, alwaysVisible: true,  category: 'footer' },
  { id: 'footer',          label: 'Theme footer',             studioControllable: false, alwaysVisible: true,  category: 'footer' },
];

/** Registry ids that participate in order but are not Studio-controllable. */
const NON_STUDIO_REGISTRY_IDS = new Set<string>(['blindSpots']);

/** Feasibility-core renderer sections — body ids derived from registry. */
function buildFeasibilityRendererSections(): RendererSectionEntry[] {
  const fromRegistry = getSectionsForType('feasibility').map(def => ({
    id: def.sectionId,
    label: def.defaultLabel,
    studioControllable: !NON_STUDIO_REGISTRY_IDS.has(def.sectionId),
    alwaysVisible: def.sectionId === 'blindSpots',
    category: categoryForSectionId(def.sectionId),
  }));
  return [RENDERER_ONLY_COVER, ...fromRegistry, ...RENDERER_ONLY_TAIL];
}

const FEASIBILITY_RENDERER_SECTIONS: RendererSectionEntry[] = buildFeasibilityRendererSections();

/** Nested charts/tables controllable independently in Template Studio. */
const SUB_SECTION_ENTRIES: RendererSectionEntry[] = [
  { id: 'scoreDrivers',        label: 'Score Driver Waterfall',     studioControllable: false, alwaysVisible: false, category: 'sub', parentSectionId: 'projectIntentOverview' },
  { id: 'dataQualityNotes',    label: 'Data Quality Notes',         studioControllable: false, alwaysVisible: false, category: 'sub', parentSectionId: 'appendix' },
  { id: 'scoreProvenance',     label: 'Score Provenance',           studioControllable: false, alwaysVisible: false, category: 'sub', parentSectionId: 'radarChart' },
];

// Every non-feasibility type whose section ids must exist in RENDERER_SECTIONS.
// `financial` was omitted (audit D20, 2026-08-19), so its ten body sections
// (decisionBand, financialSummary, capexOpexBreakdown, cashflowStream,
// monteCarloBands, riskAdjustedWacc, sensitivity, taxAndCommercial,
// financialActionPlan …) were invisible to Template Studio and to every
// consumer derived from this list, even though the DOCX/PPTX/view renderers
// all emit them.
const EXTRA_REPORT_TYPES: ReportTypeId[] = [
  'environmental', 'power-availability', 'water-resource', 'test-fit', 'interconnection',
  'supply-chain-risk', 'public-sentiment', 'custom-brief', 'financial', 'executive-summary',
];

function buildExtendedRendererSections(): RendererSectionEntry[] {
  const out: RendererSectionEntry[] = [...FEASIBILITY_RENDERER_SECTIONS, ...SUB_SECTION_ENTRIES];
  const seen = new Set(out.map(s => s.id));
  for (const type of EXTRA_REPORT_TYPES) {
    for (const def of getSectionsForType(type)) {
      if (seen.has(def.sectionId)) continue;
      seen.add(def.sectionId);
      out.push({
        id: def.sectionId,
        label: def.defaultLabel,
        studioControllable: true,
        alwaysVisible: false,
        category: def.sectionId === 'appendix' ? 'appendix' : 'body',
      });
    }
  }
  return out;
}

/** Canonical list — feasibility core + all non-feasibility section ids. */
export const RENDERER_SECTIONS: readonly RendererSectionEntry[] = buildExtendedRendererSections();

/**
 * TSP-6 — derived Set of renderer-only ids the Studio's SectionsTab
 * MUST filter out. Sibling to RENDERER_SECTIONS so adding a
 * renderer-only id in one place automatically flows to the Studio
 * filter.
 *
 * The registry-parity test asserts this set agrees with the registry,
 * so a contributor that adds a new renderer-only section but forgets
 * to flag `studioControllable: false` gets a CI failure.
 */
export const NON_STUDIO_SECTIONS: ReadonlySet<string> = new Set(
  RENDERER_SECTIONS.filter(s => !s.studioControllable).map(s => s.id),
);

/**
 * TSP-5 — the Studio's controllable subset. Convenience export so
 * `SectionsTab.resolveSections` and other Studio surfaces can read
 * the same source as the renderer parity tests.
 */
export const STUDIO_CONTROLLABLE_SECTIONS: readonly RendererSectionEntry[] =
  RENDERER_SECTIONS.filter(s => s.studioControllable);

/**
 * O(1) id lookup. Returns undefined for unknown ids (custom sections,
 * report-type-specific ids that aren't in the canonical feasibility
 * registry).
 */
const REGISTRY_BY_ID: Map<string, RendererSectionEntry> = new Map(
  RENDERER_SECTIONS.map(s => [s.id, s]),
);
export function rendererSectionById(id: string): RendererSectionEntry | undefined {
  return REGISTRY_BY_ID.get(id);
}

/**
 * Returns true when the id is a registry entry flagged
 * `studioControllable: true`. False for renderer-only ids AND for ids
 * not in the registry at all (e.g. custom user sections — those flow
 * through the `customSections[]` array, not the canonical Studio
 * list).
 */
export function isStudioControllable(id: string): boolean {
  return REGISTRY_BY_ID.get(id)?.studioControllable === true;
}

/** Sub-section ids (nested charts/tables) valid in template section order. */
export const SUB_SECTION_IDS: ReadonlySet<string> = new Set(
  SUB_SECTION_ENTRIES.map(s => s.id),
);

/** Template-order ids that may lack a gen-time `report.sectionOrder` entry. */
export const LIFTED_ORDER_IDS: ReadonlySet<string> = new Set([
  ...SUB_SECTION_IDS,
  'climateProfile',
]);

export function subSectionsForParent(parentId: string): readonly RendererSectionEntry[] {
  return SUB_SECTION_ENTRIES.filter(s => s.parentSectionId === parentId);
}
